import React, {useCallback, useMemo} from 'react';

import {varNode, opIndex, constNumber, opCount} from '@wandb/cg';

import * as CGReact from '../../cgreact';
import {PBSection, getSectionConfig} from '../WeavePanelBank/PBSection';
import {PanelBankSectionConfig} from '../WeavePanelBank/panelbank';
import {PanelContextProvider} from './PanelContext';
import * as Panel2 from './panel';
import {ChildPanel, ChildPanelFullConfig} from './ChildPanel';

type PanelLayoutFlowConfig = {
  pbConfig: PanelBankSectionConfig;
  panel: ChildPanelFullConfig;
};

export const PANEL_LAYOUT_FLOW_DEFAULT_CONFIG: PanelLayoutFlowConfig = {
  pbConfig: getSectionConfig([], undefined),
  panel: {
    id: 'Auto',
    input_node: varNode('any', 'item'),
    config: undefined,
    vars: {},
  },
};

const inputType = {
  type: 'list' as const,
  objectType: 'any' as const,
};

type PanelLayoutFlowProps = Panel2.PanelProps<
  typeof inputType,
  PanelLayoutFlowConfig
>;

const usePbLayoutConfig = (
  panelKeys: string[],
  config: PanelLayoutFlowConfig | undefined,
  updateConfig2: (
    change: (oldConfig: PanelLayoutFlowConfig) => Partial<PanelLayoutFlowConfig>
  ) => void
) => {
  const pbLayoutConfig = useMemo(() => {
    return getSectionConfig(panelKeys, config?.pbConfig);
  }, [config, panelKeys]);
  // Hmm... does not just depend on config, depends on rendered panelKeys.
  // TODO: don't like
  const updatePbLayoutConfig2 = useCallback(
    (change: (oldConfig: PanelBankSectionConfig) => PanelBankSectionConfig) => {
      return updateConfig2(currentConfig => {
        const gridConfig = getSectionConfig(panelKeys, currentConfig.pbConfig);
        const newLayoutConfig = change(gridConfig);
        return {...currentConfig, pbConfig: newLayoutConfig};
      });
    },
    [panelKeys, updateConfig2]
  );
  return {pbLayoutConfig, updatePbLayoutConfig2};
};

const usePanelLayoutFlowCommon = (props: PanelLayoutFlowProps) => {
  const config = props.config ?? PANEL_LAYOUT_FLOW_DEFAULT_CONFIG;
  const {updateConfig2} = props;
  if (updateConfig2 == null) {
    throw new Error('PanelLayoutFlow requires updateConfig2');
  }
  return useMemo(
    () => ({
      config,
      updateConfig2,
    }),
    [config, updateConfig2]
  );
};

export const PanelLayoutFlowConfigComp: React.FC<PanelLayoutFlowProps> =
  props => {
    return <div>No config yet</div>;
  };

const PanelLayoutFlowItem: React.FC<
  PanelLayoutFlowProps & {itemIndex: number}
> = props => {
  const {config} = usePanelLayoutFlowCommon(props);
  const {itemIndex} = props;
  const itemNode = opIndex({
    arr: props.input,
    index: constNumber(itemIndex),
  });

  const newVars = useMemo(() => {
    return {
      item: itemNode,
    };
  }, [itemNode]);

  return (
    <PanelContextProvider newVars={newVars}>
      <ChildPanel
        config={config.panel}
        updateConfig={() => console.log('UPDATE CONFIG IN PANEL FLOW')}
      />
    </PanelContextProvider>
  );
};

export const PanelLayoutFlow: React.FC<PanelLayoutFlowProps> = props => {
  const {config} = props;
  const {updateConfig2} = usePanelLayoutFlowCommon(props);

  const countNode = useMemo(() => {
    return opCount({arr: props.input});
  }, [props.input]);
  const countQuery = CGReact.useNodeValue(countNode);
  const count = countQuery.result ?? 10;
  const panels = useMemo(() => {
    return [...Array(count).keys()].map(i => i.toString());
  }, [count]);

  const {pbLayoutConfig, updatePbLayoutConfig2} = usePbLayoutConfig(
    panels,
    config,
    updateConfig2
  );

  return (
    <PBSection
      mode="flow"
      config={pbLayoutConfig}
      updateConfig2={updatePbLayoutConfig2}
      renderPanel={panel => (
        <PanelLayoutFlowItem {...props} itemIndex={parseInt(panel.id, 10)} />
      )}
    />
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'LayoutFlow',
  ConfigComponent: PanelLayoutFlowConfigComp,
  Component: PanelLayoutFlow,
  inputType,
  hidden: true,
};
